import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { from, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Product } from '../../../../models/product.model';
import { OrderItemDelivery } from '../../../../models/order-item-delivery.model';
import { Order } from '../../../../models/order.model';
import { OrderItem } from '../../../../models/order-item.model';
import { OrderService } from '../../../../services/order.service';
import { AuthService } from '../../../../services/auth.service';
import { OrderTypes } from '../../../../enums/order-types';
import { AddressService } from '../../../../services/address.service';
import { Address } from '../../../../models/address.model';
import {
  ITEM_COMPLETE_STATE,
  QUOTE_STATE,
} from '../../../../constants/order-states';
import {
  BACKORDER_ALL_ITEMS,
  BACKORDER_PARTIAL_ITEMS,
} from '../../../../constants/texts';
import { ModalService } from '../../../../services/modal.service';
import { getCustomizationsTotal, isEmptyArray } from '../../../../shared/utils';
import { Customization } from '../../../../interfaces/customization';
import { AlertType } from '../../../../enums/alert-type';
import { ShippingMethod } from '../../../../enums/shipping-method';
import { OrderItemSku } from '../../../../models/order-item-sku.model';
import { TranslateService } from '../../../../services/translate.service';
import { SKU } from '../../../../models/sku.model';
import {
  TrackingNumber,
  UNKNOWN_SLUG,
} from '../../../../interfaces/tracking-number';
import * as ORDER_STATES from '../../../../constants/order-states';
import { TrackingStates } from '../../../../enums/tracking-states';
import {
  computeDiscountPrice,
  computeMaxQuantity,
  computeShipmentLabel,
} from '../../../../shared/helpers';
import { PaymentService } from '../../../../services/payments.service';
import { MobileCheckServiceService } from '../../../../services/mobile-check-service.service';
import { VariationInfo } from '../../../../interfaces/variant-info';

@Component({
  selector: 'app-product-order-item-delivery',
  templateUrl: './product-order-item-delivery-component.html',
  styleUrls: ['./product-order-item-delivery-component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProductOrderItemDeliveryComponent implements OnInit {
  @Input() product: Product;

  @Input() delivery: OrderItemDelivery;

  @Input() order: Order;

  @Input() orderItem: OrderItem;

  @Input() canRemoveAddress = false;

  @Input() readonly = false;

  @Input() showBudget = false;

  @Input() showEditControls = false;

  @Input() showTotalInline = false;

  @Input() isLastShipment = false;

  @Input() isFirstShipment = false;

  @Input() viewType: string;

  @Input() totals: number;

  @Input() paidQuantity: number;

  @Input() showTrackingInfo = false;

  @Input() updatingTrackingInfo = false;

  // @Output() addressRemoved = new EventEmitter<void>(); // output event emits when delivery is removed

  // @Output() quantityChanged = new EventEmitter<void>(); // output event emits when delivery quantity is changed

  // @Output() addressChanged = new EventEmitter<void>(); // output event emits when address is changed

  @Output() onRemove = new EventEmitter<OrderItem>();

  @Output() customizationChanged = new EventEmitter<void>(); // emits when customization data changed

  @Output() deliverToChanged = new EventEmitter<void>(); // emits when deliver_to field changed

  @Output() changed = new EventEmitter<OrderItem>();

  @Output() refreshStatus = new EventEmitter<void>();

  shippingMethods: any[] = [];
  isSingleShippingMethod = false;

  addresses: Address[] = [];

  private backOrderWarning = '';

  trackingNumbers: TrackingNumber[] = [];
  status: string;
  trackingData: string;
  show = 'text';

  updatingDeliveryId: number = null;

  variationInfos: VariationInfo[] = [];

  _refreshingTrackingStatus = false;
  @Input() set refreshingTrackingStatus(val: boolean) {
    if (val === false && this._refreshingTrackingStatus) {
      // redraw tracking data when refreshing is finished;
      this.initTrackingData();
    }
    this._refreshingTrackingStatus = val;
  }

  constructor(
    private orderService: OrderService,
    private authService: AuthService,
    private addressService: AddressService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private ngbModalService: NgbModal,
    private paymentService: PaymentService,
    private mobileCheck: MobileCheckServiceService
  ) {}

  ngOnInit(): void {
    // this.configService.getShippingMethods().pipe( take(1))
    //     .subscribe(data => {
    //         this.shippingMethods = data;
    //     });
    this.calcAvailableShipmentMethods();

    this.addresses = this.addressService.addresses;

    this.detectBackorder();

    this.initTrackingData();

    this.variationInfos = this.computeVariationInfo();
  }

  private initTrackingData() {
    this.trackingNumbers = this.delivery.normalizedTrackingNumbers;

    if (this.trackingNumbers.length === 0) {
      // tracking data not available
      this.trackingData = 'N/A';
      this.status =
        this.order.state === ORDER_STATES.COMPLETE_STATE
          ? TrackingStates.DELIVERED
          : TrackingStates.PENDING;
    } else if (this.trackingNumbers.length === 1) {
      const trackingNumber = this.trackingNumbers[0];

      // single tracking number case
      this.status =
        trackingNumber.slug !== UNKNOWN_SLUG
          ? trackingNumber.status
          : this.order.state === ORDER_STATES.COMPLETE_STATE
            ? TrackingStates.DELIVERED
            : TrackingStates.PENDING;
      this.show = 'link';
      if (this.validateTrackingNumber(this.trackingNumbers[0].number)) {
        this.show = 'vendor';
        this.trackingData = this.trackingNumbers[0].number;
      }
    } else {
      // multiple tracking case

      // find global tracking status
      this.status =
        this.trackingNumbers.filter(
          (t) =>
            t.status === TrackingStates.DELIVERED && t.slug !== UNKNOWN_SLUG
        ).length === this.trackingNumbers.length
          ? TrackingStates.DELIVERED
          : TrackingStates.IN_TRANSIT;
      this.show = 'modal';

      this.trackingNumbers.forEach((tracking) => {
        tracking.mode = this.validateTrackingNumber(tracking.number)
          ? 'vendor'
          : 'link';
      });
    }
  }

  validateTrackingNumber(value: string): boolean {
    return value === 'MISC' || value === 'Call Client Service';
  }

  // showDetails(content) {
  //   this.ngbModalService.open(content).result.then(
  //     () => {},
  //     () => {}
  //   );
  // }

  isDelivered(val: TrackingStates): boolean {
    return val === TrackingStates.DELIVERED;
  }

  /**
   * checks whether item can be purchased
   * in case of BUYING_WINDOW project  ow_purchasable == false - purchase is not available
   */
  get canPurchase(): boolean {
    if (!this.order) {
      return false;
    }

    if (this.paymentService.hasFailedTransactions) {
      return false;
    }

    return (
      !this.product.isCancelled &&
      this.orderItem.deliveries.length !== 0 &&
      (this.order.type === OrderTypes.ECOMMERCE_TYPE
        ? this.orderItem.init_quantity + this.delivery.quantity > 0 ||
          this.product.inStock
        : this.product.ow_purchasable === true)
    );
  }

  updateVariation(info: VariationInfo) {
    this.orderItem.updateDelivery(this.delivery);
    this.changed.emit(this.orderItem);
  }

  updateAddressData(address: Address) {
    if (this.canOrder && address) {
      const userRoles = {
        canOrder: true,
        isEmployee: this.authService.isEmployee,
      };
      const updateShippingMethod =
        this.delivery.isInternational !== address.isInternational;
      this.delivery.data = this.delivery.data || {};
      this.delivery.unchanged = false;
      this.delivery.setAddress(
        address,
        this.authService.user,
        this.order.isCustom,
        userRoles
      );
      this.calcAvailableShipmentMethods(updateShippingMethod);

      this.orderItem.updateDelivery(this.delivery);
      this.changed.emit(this.orderItem);
      // this.addressChanged.emit();
    }
  }

  updateShippingMethod(method: ShippingMethod): void {
    if (!method) {
      return;
    }

    if (
      [ShippingMethod.NextDay, ShippingMethod.TwoDay].includes(method) &&
      !this.product.shippingAlertShown
    ) {
      this.modalService.showAlert(
        {
          type: AlertType.Prompt,
          headerClass: 'state modal-warning',
          title: 'Alert!',
          message:
            'Orders for expedited shipping must be placed before 12:00 PM CST for same day processing. Orders placed after 12:00 PM CST will be shipped next business day with the chosen shipping method.',
          actionLabel: null,
        },
        'md',
        true
      );

      this.product.shippingAlertShown = true;
    }

    this.delivery.method = method;
    this.orderItem.updateDelivery(this.delivery);
    this.changed.emit(this.orderItem);
  }

  removeAddress() {
    this.orderItem.removeDelivery(this.delivery);
    this.changed.emit(this.orderItem);
  }

  get completed(): boolean {
    return this.orderItem.state === ITEM_COMPLETE_STATE;
  }

  onQuantityChanged(event) {
    const newValue: number = +event.target.value || 0;
    if (this.orderItem.isAvailable) {
      // ensure quantity is not null
      this.delivery.quantity = this.orderItem.validateDeliveryQuantity(
        this.delivery,
        newValue
      );
      // this.checkQuantity();

      this.detectBackorder();
    }
  }

  onQuantityBlur() {
    // emit change event only after blur event  to keep focus on input
    this.deliveryChanged();
    return true;
  }

  private deliveryChanged() {
    this.orderItem.changed = true;
    this.changed.emit(this.orderItem);
  }

  onHandleRemove() {
    this.onRemove.emit(this.orderItem);
  }

  get hideShippingMethod(): boolean {
    return this.order.type === OrderTypes.PREORDER_TYPE;
  }

  private detectBackorder() {
    if (this.order.type === OrderTypes.ECOMMERCE_TYPE) {
      const pendingQuantity = this.orderItem.getPendingQuantity();
      if (pendingQuantity) {
        const inStockQuantity = this.orderItem.getInStockQuantity();

        if (inStockQuantity === 0 && this.delivery.quantity > 0) {
          // all items go to backorder
          this.backOrderWarning = BACKORDER_ALL_ITEMS;
        } else if (inStockQuantity < this.delivery.quantity) {
          // partial items go to backorder
          const backOrderAmount = this.delivery.quantity - inStockQuantity;
          this.backOrderWarning = BACKORDER_PARTIAL_ITEMS.replace(
            '${0}',
            '' + backOrderAmount
          );
        } else {
          this.backOrderWarning = '';
        }
      }
    }
  }

  addCustomization() {
    const data: {
      maxQTY: number;
      productCustomization: Customization;
      deliveryCustomizations: Customization[];
      hasVariations: boolean;
    } = {
      maxQTY: this.orderItem.maxAvailableQuantity,
      productCustomization: this.orderItem.product.customization,
      deliveryCustomizations: this.delivery.customizations,
      hasVariations: this.orderItem.product.hasVariations,
    };

    this.modalService.showProductCustomizationModal(data).then((result) => {
      if (result) {
        this.delivery.quantity = getCustomizationsTotal(result);
        this.delivery.customizations = result;
        this.orderItem.calculateQuantity();

        // this.deliveryChanged();
        this.customizationChanged.emit();
      }
    });
  }

  get canCustomize(): boolean {
    return (
      !this.product.isCancelled &&
      this.order.isDecisionPoint !== true &&
      this.authService.canEditOrder(this.order) &&
      !this.paymentService.hasFailedTransactions
    );
  }
  onQuantityClicked(e: Event) {
    if (this.delivery.hasCustomization) {
      setTimeout(() => {
        this.addCustomization();
      }, 500);
    }
  }

  get canEditDeliveryTo(): boolean {
    return (
      !this.product.isCustomizable &&
      this.order.state === QUOTE_STATE &&
      this.authService.canEditOrder(this.order)
    );
  }

  get canEditSAP(): boolean {
    return this.order.state === QUOTE_STATE && this.order.isCustom;
  }

  editDeliverTo(delivery: OrderItemDelivery) {
    if (!this.canEditDeliveryTo) {
      return;
    }

    this.modalService
      .showShipToModal(this.order, this.orderItem, delivery)
      .then((retAttention) => {
        if (retAttention) {
          this.deliverToChanged.emit();
        }
      });
  }

  editSAP(delivery: OrderItemDelivery) {
    if (!this.canEditSAP) {
      return;
    }

    const costCenter = delivery.cost_center || this.order.costCenter;

    from(this.modalService.showSelectSAPModal(costCenter))
      .pipe(
        switchMap((retCostCenter) => {
          if (!retCostCenter) {
            return of(null);
          }

          this.delivery.cost_center = retCostCenter;
          this.updatingDeliveryId = delivery.id;
          return this.orderService.updateOrderItem(this.order, this.orderItem);
        })
      )
      .subscribe((retOrder) => {
        if (retOrder) {
          const retOrderItem = retOrder.items.find(
            (i) => i.id === this.orderItem.id
          );
          if (retOrderItem) {
            const retDelivery = retOrderItem.deliveries.find(
              (d) => d.id === delivery.id
            );
            if (retDelivery) {
              this.delivery = new OrderItemDelivery(retDelivery);
              this.deliverToChanged.emit();
            }
          }
        }

        this.updatingDeliveryId = null;
      });
  }

  public getShipToLabel(delivery: OrderItemDelivery): string {
    return computeShipmentLabel(delivery, this.order.attr_attention);
  }

  public getSapInfo(delivery: OrderItemDelivery): string {
    return delivery.cost_center
      ? delivery.cost_center.getLongAccount()
      : this.order.costCenter.getLongAccount();
  }

  private calcAvailableShipmentMethods(updateShippingMethod = false) {
    if (this.delivery.isInternational) {
      this.delivery.method = ShippingMethod.International;
      this.isSingleShippingMethod = true;
    } else {
      const methods = [];
      for (const [key, value] of Object.entries(
        this.product.shipping_options
      )) {
        if (value !== -1 && key !== ShippingMethod.International) {
          methods.push(key);
        }
      }

      if (methods.length > 0) {
        if (methods.length === 1) {
          this.isSingleShippingMethod = true;
          this.delivery.method = methods[0];
        } else {
          // pickup ground or first one
          if (updateShippingMethod) {
            // do not change shipping method after load
            this.delivery.method = methods.includes(ShippingMethod.Ground)
              ? ShippingMethod.Ground
              : methods[0];
          } else {
            // check if delivery method is allowed
            if (!methods.includes(this.delivery.method)) {
              this.delivery.method = methods[0];
            }
          }
          this.isSingleShippingMethod = false;
          this.shippingMethods = methods;
        }
      }
    }
  }

  findOrderItemSku(skuId: number): OrderItemSku {
    if (!this.orderItem || isEmptyArray(this.orderItem.skus)) {
      return;
    }

    return this.orderItem.skus.find((s) => s.sku_id === skuId);
  }

  getVariationLabel(skuId: number): string {
    const productSku = this.getProductSku(skuId);
    if (!productSku) {
      return;
    }

    const labels = productSku.attributes
      .sort((a, b) => {
        return a.label > b.label ? 1 : b.label > a.label ? -1 : 0;
      })
      .map((attribute) => {
        const label = this.translateService.translate(attribute.label);
        return `${label}: ${attribute.options[0].label}`;
      });

    return labels.join(', ');
  }

  getVariationPrice(skuId: number): number {
    let price = 0;
    let skyQuantity = 0;

    const productSKU = this.getProductSku(skuId);
    if (!productSKU) {
      return price;
    }

    skyQuantity = this.delivery?.quantity || 0;

    // if (this.product.isBuyingWindow) {
    //   if (this.product?.hasVariations && !isEmptyArray(this.product.skus)) {
    //     skyQuantity =
    //       this.product.skus.reduce(
    //         (total, i) => (total += i.aggregatedQuantity),
    //         0
    //       ) || 0;
    //   }
    // } else {
    //
    // }
    //
    price = this.product.getPrice(skyQuantity, productSKU);
    return computeDiscountPrice(price, this.discount);
  }

  // getVariationTotal(sku: OrderItemSku): number {
  //   const productSKU = this.getProductSku(sku);
  //   if (!productSKU) {
  //     return 0;
  //   }
  //
  //   const skuQuantity = sku?.quantity || 0;
  //
  //   let aggregatedQuantity = sku?.quantity || 0;
  //   if (this.product.hasVariations && this.product.isBuyingWindow) {
  //     aggregatedQuantity = productSKU?.aggregated_quantity || 0;
  //   }
  //
  //   return (
  //     computeDiscountPrice(
  //       productSKU.getPrice(aggregatedQuantity),
  //       this.discount
  //     ) * skuQuantity
  //   );
  // }

  getProductSku(skuId: number): SKU {
    if (!skuId) {
      return;
    }
    return this.orderItem.product.skus.find((s) => s.id === skuId);
  }

  showDPFaqDialog() {
    this.modalService.showDecisionPointFaq(this.order.attr_decision_point_name);
  }

  get discount(): number {
    if (this.orderItem) {
      return this.orderItem.productDiscount;
    }

    return this.product?.discount || 0;
  }

  get canOrder(): boolean {
    return this.authService.canOrder;
  }

  get mobile(): boolean {
    return this.mobileCheck.mobile;
  }

  get mobileDevice(): boolean {
    return this.mobileCheck.mobileDevice;
  }

  get orderContainsSap(): boolean {
    if (this.authService.isOneOfWholesaler) {
      return false;
    }

    return !this.order.isWSLR;
  }

  private getVariationLabelForProduct(sku: SKU): string[] {
    if (!sku) {
      return [];
    }
    return sku.sortedAttributes.map((attribute) => {
      const label = this.translateService.translate(attribute.label);
      return `${label}: ${attribute.options[0].label}`;
    });
  }

  private computeVariationInfo(): VariationInfo[] {
    if (!this.product?.hasVariations) {
      return [];
    }

    const variationInfos: VariationInfo[] = [];
    for (const sku of this.product.skus) {
      const label = this.getVariationLabelForProduct(sku).join(', ');
      variationInfos.push({
        skuId: sku.id,
        sku: sku,
        label: label,
        skuLabels: this.getVariationLabelForProduct(sku),
      });
    }
    return variationInfos;
  }

  showMinOrderInBody(sku: SKU): boolean {
    if (this.readonly) {
      return false;
    }
    if (!this.product.isBuyingWindow) {
      return false;
    }

    if (this.product.hasVariations) {
      const showMaxQuantity =
        computeMaxQuantity(this.product, sku) > 0 || this.product.has_inventory;
      return !(this.product.getGroupMinQuantity() > 0) || showMaxQuantity;
    }
    return true;
  }

  get showInputLabel(): boolean {
    // if (this.product.hasVariations) {
    //   return true;
    // }
    // return this.isFirstShipment;
    return true;
  }

  get assignedSku(): SKU {
    if (!this.product?.hasVariations) {
      return undefined;
    }

    return this.product.skus.find((s) => s.id === this.delivery.sku_id);
  }

  getVariationsForDelivery(delivery: OrderItemDelivery): OrderItemSku[] {
    if (!this.product.hasVariations) {
      return [];
    }

    if (!isEmptyArray(delivery.variationsInDelivery)) {
      const variations: OrderItemSku[] = [];
      delivery.variationsInDelivery.forEach((variationId) => {
        const sku = this.orderItem.skus.find((s) => s.sku_id === variationId);
        if (sku) {
          variations.push(sku);
        }
      });
      return variations;
    }

    return this.orderItem.skus.filter((s) => s.sku_id === delivery.sku_id);
  }

  // deliveryHasVariations(delivery: OrderItemDelivery): boolean {
  //   return !isEmptyArray(this.getVariationsForDelivery(delivery))
  // }

  get shippingQuantity(): number {
    if (this.paidQuantity) {
      return this.paidQuantity;
    }

    // if (this.product.hasVariations) {
    //   const variations = this.orderItem.skus.find((s) => s.sku_id === this.delivery.sku_id);
    //   return variations?.quantity || 0;
    // }

    return this.delivery.quantity;
  }

  get selectedSku(): SKU {
    if (!this.product.hasVariations) {
      return undefined;
    }
    if (this.delivery?.sku_id) {
      return this.product.skus.find((s) => s.id === this.delivery.sku_id);
    }
  }

  get selectedSkusLabel(): string {
    const labels = this.selectedSkusLabels;
    if (isEmptyArray(labels)) {
      return '';
    }
    return labels.join('\n');
  }

  get selectedSkusLabels(): string[] {
    const selectedSku = this.selectedSku;
    if (!selectedSku) {
      return [];
    } else {
      return this.getVariationLabelForProduct(selectedSku);
    }
  }

  get isLegacySku(): boolean {
    //  total amount of skus in order item with qty > 0
    const orderItemSkuLength = isEmptyArray(this.orderItem.skus)
      ? 0
      : this.orderItem.skus.length;

    // total amount of deliveries in order item with qty > 0
    const orderItemDeliveryLength = isEmptyArray(this.orderItem.deliveries)
      ? 0
      : this.orderItem.deliveries.filter((d) => d.quantity > 0).length;

    return orderItemDeliveryLength === 1 && orderItemSkuLength > 1;
  }

  getLabelForLegacySku(skuId: number): string {
    if (!skuId) {
      return '';
    }

    const productSku = this.orderItem.product.skus.find((s) => s.id === skuId);
    if (!productSku) {
      return '';
    }

    return this.getVariationLabelForProduct(productSku).join(', ');
  }
}
